import React, { useState } from 'react';
import moment from 'moment';
import { useMutation } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
import IconButton from '@material-ui/core/IconButton';

import StatusBadge from 'designV2/components/StatusBadge';
import DialogShell from 'designV2/components/DialogShell';
import './paymentDetails.css';

const UPDATE_PAYMENT = gql`
	mutation manualResolvePayment($_id: ID!) {
		manualResolvePayment(_id: $_id) {
			_id
			status
		}
	}
`;

const STATUS_TONES = {
	PAID: 'success',
	UNPAID: 'warning',
	FAILED: 'danger',
	FAILED_DUE_TO_REJECTION: 'danger',
};

const readableError = (error) => {
	if (!error) return '';
	if (error.graphQLErrors && error.graphQLErrors.length > 0) {
		return error.graphQLErrors[0].message;
	}
	return error.message || 'Something went wrong, please try again.';
};

const CopyableField = ({ label, value }) => {
	const [copied, setCopied] = useState(false);
	if (!value) return null;

	const handleCopy = () => {
		if (!navigator.clipboard) return;
		navigator.clipboard.writeText(value).then(() => {
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		});
	};

	return (
		<div className="paymentDetailsField">
			<span className="paymentDetailsLabel">{label}</span>
			<span className="paymentDetailsValueRow">
				<span className="paymentDetailsMono">{value}</span>
				<IconButton size="small" className="paymentDetailsCopyButton" onClick={handleCopy} title="Copy">
					{copied ? <CheckIcon fontSize="small" /> : <FileCopyIcon style={{ fontSize: 16 }} />}
				</IconButton>
			</span>
		</div>
	);
};

/**
 * PaymentDetails - content-only (no title, no close button, no Dialog
 * wrapper of its own) — every call site renders it inside a shared
 * DialogShell, the same as OrderDetails. Shared across the super admin's
 * Pay/Payments/TransactionDetails screens and the shop admin's own
 * Payments screen, so the prop contract ({ payment, close, hasResolver,
 * refetch }) is unchanged even though the markup is all new.
 */
const PaymentDetails = ({ payment, close, hasResolver, refetch }) => {
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [error, setError] = useState('');

	const [manualResolvePayment, { loading: resolving }] = useMutation(UPDATE_PAYMENT);

	const resolve = () => {
		manualResolvePayment({ variables: { _id: payment._id } })
			.then(() => {
				setConfirmOpen(false);
				if (refetch) refetch();
				close();
			})
			.catch((err) => setError(readableError(err)));
	};

	return (
		<div className="paymentDetailsBody">
			<div className="paymentDetailsTopRow">
				{payment.status && (
					<StatusBadge
						status={STATUS_TONES[payment.status] || 'neutral'}
						label={payment.status.replace(/_/g, ' ').toLowerCase()}
					/>
				)}
				{typeof payment.amount === 'number' && !Number.isNaN(payment.amount) && (
					<span className="paymentDetailsAmount">${payment.amount.toFixed(2)}</span>
				)}
			</div>

			<div className="paymentDetailsGrid">
				<CopyableField label="Payment ID" value={payment._id} />
				{payment.shop && (
					<div className="paymentDetailsField">
						<span className="paymentDetailsLabel">Shop</span>
						<span className="paymentDetailsValue">{payment.shop.name}</span>
					</div>
				)}
				{payment.driver && (
					<div className="paymentDetailsField">
						<span className="paymentDetailsLabel">Driver</span>
						<span className="paymentDetailsValue">{payment.driver.fullName}</span>
					</div>
				)}
				{payment.type && (
					<div className="paymentDetailsField">
						<span className="paymentDetailsLabel">Type</span>
						<span className="paymentDetailsValue">{payment.type.replace(/_/g, ' ').toLowerCase()}</span>
					</div>
				)}
				{payment.for && (
					<div className="paymentDetailsField">
						<span className="paymentDetailsLabel">For</span>
						<span className="paymentDetailsValue">{payment.for.toLowerCase()}</span>
					</div>
				)}
				<div className="paymentDetailsField">
					<span className="paymentDetailsLabel">Created</span>
					<span className="paymentDetailsValue">{moment(payment.createdAt).format('MMM D, YYYY hh:mm A')}</span>
				</div>
				{payment.updatedAt && (
					<div className="paymentDetailsField">
						<span className="paymentDetailsLabel">Updated</span>
						<span className="paymentDetailsValue">{moment(payment.updatedAt).format('MMM D, YYYY hh:mm A')}</span>
					</div>
				)}
				<CopyableField label="Transaction ref" value={payment.transactionId && payment.transactionId.transactionId} />
				<CopyableField label="Trip ID" value={payment.trip && payment.trip._id} />
				<CopyableField label="Order ID" value={payment.order && payment.order._id} />
			</div>

			{payment.description && (
				<div className="paymentDetailsField">
					<span className="paymentDetailsLabel">Description</span>
					<span className="paymentDetailsValue">{payment.description}</span>
				</div>
			)}

			{error && (
				<div className="dsFormMessage error" role="alert">
					{error}
				</div>
			)}

			{hasResolver && payment.status !== 'PAID' && (
				<div className="paymentDetailsFooter">
					<button type="button" className="paymentDetailsResolveButton" onClick={() => setConfirmOpen(true)}>
						Resolve
					</button>
				</div>
			)}

			<DialogShell
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				title="Resolve payment"
				size="sm"
				actions={[
					{ label: 'Cancel', onClick: () => setConfirmOpen(false) },
					{ label: 'Resolve', onClick: resolve, variant: 'primary', loading: resolving },
				]}
			>
				<div className="paymentDetailsConfirmBody">Do you want to resolve this payment?</div>
			</DialogShell>
		</div>
	);
};

export default PaymentDetails;
